import { Box, Icon, SelectChangeEvent, Typography } from '@mui/material';
import usePagination from '@mui/material/usePagination';
import { SxProps, Theme } from '@mui/system';
import { FC, useEffect } from 'react';
import {
  StyledEllipsis,
  StyledLabel,
  StyledMenuItem,
  StyledPageButtonSelected,
  StyledPageButtonUnselected,
  StyledPageClickerButton,
  StyledSelect,
  StyledUnderline,
} from './CfPagination.styles';

export const defaultPageSizeOptions = [25, 50, 100, 250];

export interface CfPaginationProps {
  pageIndex: number;
  pageSize: number;
  totalItemCount: number;
  pageSizeOptions?: number[];
  startingPageIndex?: number;
  siblingCount?: number;
  /**
   * Callback fired when pagination is initialized.
   *
   * @param {number} pageIndex The page index selected.
   * @param {number} pageSize The page size selected.
   */
  onInit?: (pageIndex: number, pageSize: number) => void;
  /**
   * Callback fired when pagination is changed.
   *
   * @param {number} pageIndex The page index selected.
   * @param {number} pageSize The page size selected.
   * @param {string} eventType The type of event.
   */
  onChange?: (pageIndex: number, pageSize: number, eventType: 'pageIndex' | 'pageSize') => void;
  sx?: SxProps<Theme>;
}

/**
 * @param pageIndex - The index of the current page
 * @param pageSize - The number of items being displayed on each page
 * @param totalItemCount - The total number of items on all pages
 * @param pageSizeOptions - Array of numbers reprenting the choices for how many items are on a page
 * @param startingPageIndex - (Not used)
 * @param siblingCount - Number of always visible pages before and after the current page
 * @param onInit - Method to call on initialization
 * @param onChange - Method to call on page change
 * @param sx - Styling of the box containing the component
 */
export const CfPagination: FC<CfPaginationProps> = (props: CfPaginationProps) => {
  const pageSizeOptions = props.pageSizeOptions ?? defaultPageSizeOptions;
  const selectedPageSize = pageSizeOptions.find((pageSize) => props.pageSize <= pageSize) ?? pageSizeOptions[0];

  const totalPages = Math.ceil(props.totalItemCount / selectedPageSize);

  const pagination = usePagination({
    count: totalPages,
    page: props.pageIndex + 1,
    boundaryCount: 1,
    siblingCount: props.siblingCount ?? 2,
    onChange: (event, page) => {
      const pageIndex = page - 1;
      props.onChange?.(pageIndex, props.pageSize, 'pageIndex');
    },
  });

  useEffect(() => {
    props.onInit?.(props.pageIndex, props.pageSize);
  }, []);

  return (
    <Box display='flex' alignItems='baseline' sx={props.sx}>
      <Box display='flex' justifyContent='center' flexGrow={1}>
        <Box display='flex' justifyContent='center'>
          <Box display='flex' alignItems='baseline'>
            <StyledLabel sx={{ mr: 1 }}>Results Per Page:</StyledLabel>
            <StyledSelect
              variant='standard'
              disableUnderline
              value={selectedPageSize}
              onChange={(event: SelectChangeEvent<unknown>) => {
                const pageIndex = 0;
                const pageSize = event.target.value as number;
                props.onChange?.(pageIndex, pageSize, 'pageSize');
              }}
            >
              {pageSizeOptions.map((pageSize: number) => (
                <StyledMenuItem value={pageSize} key={pageSize}>
                  {pageSize}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>
        </Box>
      </Box>
      <Box display='flex' justifyContent='center' flexGrow={2}>
        <Box display='flex' justifyContent='center' alignItems='baseline'>
          {pagination.items.map(({ page, type, selected, ...item }) => {
            let children = null;
            const key = type + page;

            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
              children = <StyledEllipsis key={key}>…</StyledEllipsis>;
            } else if (type === 'page') {
              const pageIndex = (page ?? 1) - 1;
              const delta = pageIndex - props.pageIndex;
              let testId = (delta < 0 ? 'pagination-previous-' : 'pagination-next-') + Math.abs(delta);
              if (pageIndex === 0) {
                testId = 'pagination-first';
              } else if (page === totalPages) {
                testId = 'pagination-last';
              }
              children = selected ? (
                <StyledPageButtonSelected key={key} data-testid='pagination-current' {...item}>
                  {page}
                  <StyledUnderline />
                </StyledPageButtonSelected>
              ) : (
                <StyledPageButtonUnselected key={key} data-testid={testId} {...item}>
                  {page}
                </StyledPageButtonUnselected>
              );
            } else if (type === 'previous') {
              children = (
                <StyledPageClickerButton key={key} data-testid='pagination-previous-btn' {...item}>
                  <div>
                    <Icon fontSize='small' className='fa-angle-left' sx={{ mr: 1 }} />
                    <span>Previous</span>
                  </div>
                </StyledPageClickerButton>
              );
            } else if (type === 'next') {
              children = (
                <StyledPageClickerButton key={key} data-testid='pagination-next-btn' {...item}>
                  <div>
                    <span>Next</span>
                    <Icon fontSize='small' className='fa-angle-right' sx={{ ml: 1 }} />
                  </div>
                </StyledPageClickerButton>
              );
            }

            return children;
          })}
        </Box>
      </Box>

      <Box display='flex' justifyContent='center' flexGrow={1}>
        <Box display='flex' justifyContent='center'>
          <Typography variant='paragraphSmallBold' sx={{ color: (theme) => theme.palette.coolGrey[500] }}>
            Showing {Math.min(selectedPageSize * props.pageIndex + 1, props.totalItemCount)}-
            {Math.min(selectedPageSize * (props.pageIndex + 1), props.totalItemCount)} of {props.totalItemCount} Item
            {props.totalItemCount === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
